import { FIXTURE_COLLECTIONS, PRODUCT_FIXTURES } from './product.fixtures';
import { isShopifyConfigured, readShopifyConfig, type ShopifyConfig } from './shopify.client';
import { ShopifyProductRepository } from './shopify.repository';
import type { ProductPage, ProductRepository, ShopProduct } from './shopify.types';

/**
 * Curseur simulé : l'index du dernier produit servi, en clair. La page suivante
 * commence donc juste après. Un curseur illisible repart du début plutôt que de
 * renvoyer une page vide.
 */
function cursorStart(after?: string | null): number {
  if (after === undefined || after === null || after === '') return 0;
  const index = Number.parseInt(after, 10);
  return Number.isNaN(index) || index < 0 ? 0 : index + 1;
}

/** Dépôt local : aucune requête réseau, réponses immédiates. */
export class FixtureProductRepository implements ProductRepository {
  constructor(private readonly products: ShopProduct[] = PRODUCT_FIXTURES) {}

  async getFeaturedProducts(): Promise<ShopProduct[]> {
    return this.products.filter((product) => product.availableForSale).slice(0, 4);
  }

  async getProductsByHandles(handles: string[]): Promise<ShopProduct[]> {
    // Un handle inconnu est ignoré plutôt que de faire échouer toute la scène.
    return handles
      .map((handle) => this.products.find((product) => product.handle === handle))
      .filter((product): product is ShopProduct => product !== undefined);
  }

  async getProductByHandle(handle: string): Promise<ShopProduct | null> {
    return this.products.find((product) => product.handle === handle) ?? null;
  }

  async getCollectionProducts(
    handle: string,
    first: number,
    after?: string | null,
  ): Promise<ProductPage> {
    // Collection inconnue : page vide, comme le Storefront.
    const members = FIXTURE_COLLECTIONS[handle] ?? [];
    const all = members
      .map((productHandle) => this.products.find((product) => product.handle === productHandle))
      .filter((product): product is ShopProduct => product !== undefined);

    const start = cursorStart(after);
    const size = Math.max(0, Math.trunc(first));
    const page = all.slice(start, start + size);
    const end = start + page.length;

    return {
      products: page,
      hasNextPage: end < all.length,
      endCursor: page.length > 0 ? String(end - 1) : null,
    };
  }
}

let repository: ProductRepository | null = null;

/**
 * Storefront réel dès que le domaine et le token sont renseignés, fixtures sinon.
 * Aucun composant d'interface ne sait laquelle des deux implémentations tourne.
 */
export function getProductRepository(): ProductRepository {
  if (!repository) {
    repository = isShopifyConfigured()
      ? new ShopifyProductRepository(readShopifyConfig() as ShopifyConfig)
      : new FixtureProductRepository();
  }
  return repository;
}

/** Permet aux tests d'injecter un dépôt. */
export function setProductRepository(next: ProductRepository | null): void {
  repository = next;
}
